import React, { useEffect, useState, useCallback } from "react";
import styles from "../styles/SalesMode.module.css";
import { X } from "lucide-react";
import HistoryBody from "../pages/SalesPages/HistoryBody";
import ReceivingBody from "../pages/SalesPages/ReceivingBody";
import PaymentModal from "../pages/SalesPages/PaymentModal";
import EditProductPage from "../pages/SalesPages/EditProductPage";
import DiscountModal from "../pages/SalesPages/DiscountModal";
import CashInModal from "../pages/SalesPages/CashInModal";
import CashOutModal from "../pages/SalesPages/CashOutModal";
import LockScreenModal from "../pages/SalesPages/LockScreenModal";
import ClientSelectModal from "../pages/SalesPages/ClientSelectModal";
import CommissionSelectModal from "../pages/SalesPages/CommissionSelectModal";
import UniversalProductModal from "../pages/SalesPages/UniversalProductModal";

import {
  FiClock,
  FiUser,
  FiMapPin,
  FiMenu,
  FiSearch,
  FiDownload,
  FiColumns,
  FiType,
  FiTrash2,
} from "react-icons/fi";
import { FiUsers, FiPercent, FiEdit } from "react-icons/fi";
import { FiUpload, FiXCircle, FiLock, FiBarChart2 } from "react-icons/fi";
import FastProductsModal from "./tables/customComponentsTable/FastProductsModal";
import CloseShiftModal from "../pages/SalesPages/CloseShiftModal";
import KeyboardPage from "../pages/SalesPages/KeyboardPage";

import { API_URL } from "../config/api";

const ReceivingConfirmModal = ({ open, items = [], onCancel, onConfirm }) => {
  if (!open) return null;

  const title =
    items.length <= 1
      ? "Создать приемку по товару?"
      : `Создать приемку по товарам (${items.length})?`;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <h2 style={{ marginBottom: 10 }}>{title}</h2>

        <div style={{ marginBottom: 12, opacity: 0.9 }}>
          {items.length === 0 ? (
            <div>Товары не выбраны</div>
          ) : (
            <div style={{ maxHeight: 220, overflow: "auto" }}>
              {items.map((p) => (
                <div
                  key={p.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                    padding: "6px 0",
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                  }}
                >
                  <span
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {p.name || "Без названия"}
                  </span>
                  <span style={{ opacity: 0.7, whiteSpace: "nowrap" }}></span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.modalActions}>
          <button onClick={onConfirm} disabled={items.length === 0}>
            Да, создать
          </button>
          <button onClick={onCancel}>Нет</button>
        </div>
      </div>
    </div>
  );
};

const OpenShiftModal = ({ warehouse, onClose, onOpenShift }) => {
  const [amount, setAmount] = useState("0.00");

  const handleKeyClick = (key) => {
    if (key === "C") {
      setAmount("0.00");
    } else {
      setAmount((prev) => (prev === "0.00" ? key : prev + key));
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <h2>Открыть смену</h2>
        <p>
          <strong>Склад:</strong> {warehouse?.name}
        </p>

        <div className={styles.amountDisplay}>{amount}</div>

        <div className={styles.numPad}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, ".", 0].map((n) => (
            <button key={n} onClick={() => handleKeyClick(n.toString())}>
              {n}
            </button>
          ))}
          <button onClick={() => handleKeyClick("C")}>C</button>
        </div>

        <div className={styles.modalActions}>
          <button onClick={() => onOpenShift(amount)}>Открыть смену</button>
          <button onClick={onClose}>На главную</button>
        </div>
      </div>
    </div>
  );
};

const SalesMode = ({ warehouse, seller, onClose }) => {
  const [commissionModalOpen, setCommissionModalOpen] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState({
    id: "none",
    name: "Без комиссии",
    kind: "fixed",
    value: 0,
  });
  const [universalOpen, setUniversalOpen] = useState(false);
  const [discountSelectedId, setDiscountSelectedId] = useState(null);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [discountItem, setDiscountItem] = useState(null);
  const [cashInOpen, setCashInOpen] = useState(false);
  const [cashOutOpen, setCashOutOpen] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const [holds, setHolds] = useState([]);
  const [activeHoldId, setActiveHoldId] = useState(null);

  const [cashierName, setCashierName] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const [keyboardOpen, setKeyboardOpen] = useState(false);

  const [rightMode, setRightMode] = useState("categories");
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [categoryProducts, setCategoryProducts] = useState([]);
  const [loadingCategoryProducts, setLoadingCategoryProducts] = useState(false);

  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [closeShiftOpen, setCloseShiftOpen] = useState(false);
  const [shiftError, setShiftError] = useState(null);
  const [time, setTime] = useState(new Date());
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [split, setSplit] = useState(false);
  const [shiftOpen, setShiftOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("sale");
  const [fastProductsOpen, setFastProductsOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [lastAddedId, setLastAddedId] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);

  // ✅ setProducts не использовался и сам products нигде не наполнялся → убираем, чтобы ничего не ломать

  const [receivingDraftItems, setReceivingDraftItems] = useState([]);
  const [receivingConfirmOpen, setReceivingConfirmOpen] = useState(false);

  const token = localStorage.getItem("token");

  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

  const getDiscountForItem = (item) => {
    const d = item?.discount;
    if (!d || !d.value) return { type: null, value: 0, qty: item?.qty || 0 };

    const type = d.type === "amount" ? "amount" : "percent";
    const value = Number(d.value) || 0;
    const qty = clamp(Number(d.qty || item.qty || 0), 0, Number(item.qty || 0));

    return { type, value, qty };
  };

  const calcDiscountAmount = (item) => {
    const price = Number(item?.price || 0);
    const qty = Number(item?.qty || 0);
    const { type, value, qty: dq } = getDiscountForItem(item);

    if (!type || value <= 0 || price <= 0 || qty <= 0 || dq <= 0) return 0;

    if (type === "percent") {
      const pct = clamp(value, 0, 100);
      return (price * pct) / 100 * dq;
    }

    const perUnit = clamp(value, 0, price);
    return perUnit * dq;
  };

  const calcLineTotal = (item) => {
    const price = Number(item?.price || 0);
    const qty = Number(item?.qty || 0);
    const base = price * qty;

    const discount = calcDiscountAmount(item);
    return Math.max(0, base - discount);
  };

  const calcLineBase = (item) => {
    const price = Number(item?.price || 0);
    const qty = Number(item?.qty || 0);
    return price * qty;
  };

  const pickDiscountTarget = () => {
    if (!cartItems.length) return null;

    // 1) если выбран ровно 1 чекбокс — скидка по нему
    if (discountSelectedId) {
      return cartItems.find((x) => x.id === discountSelectedId) || null;
    }

    // 2) иначе — по lastAdded
    return (
      cartItems.find((x) => x.id === lastAddedId) ||
      cartItems[cartItems.length - 1] ||
      null
    );
  };

  const openDiscountForLastItem = () => {
    if (cartItems.length === 0) {
      alert("Выберите товар для скидки");
      return;
    }

    if (selectedItems.length > 1) {
      alert("Для скидки выберите ровно 1 товар (или снимите выделение)");
      return;
    }

    const target = pickDiscountTarget();
    if (!target) return;

    setDiscountItem(target);
    setDiscountOpen(true);
  };

  const syncActiveHold = useCallback(
    (nextCartItems) => {
      if (!activeHoldId) return;

      if (!nextCartItems || nextCartItems.length === 0) {
        setHolds((prev) => prev.filter((h) => h.id !== activeHoldId));
        setActiveHoldId(null);

        setSelectedItems([]);
        setSelectAll(false);
        return;
      }

      setHolds((prev) =>
        prev.map((h) =>
          h.id === activeHoldId ? { ...h, items: nextCartItems } : h
        )
      );
    },
    [activeHoldId]
  );

  const createHold = () => {
    if (cartItems.length === 0) {
      alert("Корзина пуста");
      return;
    }

    const nextIndex = holds.length + 1;

    const newHold = {
      id: crypto.randomUUID(),
      title: `Отложка ${nextIndex}`,
      items: cartItems,
    };

    setHolds((prev) => [...prev, newHold]);

    setCartItems([]);
    setSelectedItems([]);
    setSelectAll(false);
    setActiveHoldId(null);
  };

  const switchHold = (holdId) => {
    const hold = holds.find((h) => h.id === holdId);
    if (!hold) return;

    if (activeHoldId) {
      if (cartItems.length === 0) {
        setHolds((prev) => prev.filter((h) => h.id !== activeHoldId));
        setActiveHoldId(null);
      } else {
        setHolds((prev) =>
          prev.map((h) =>
            h.id === activeHoldId ? { ...h, items: cartItems } : h
          )
        );
      }
    }

    setCartItems(hold.items);
    setSelectedItems([]);
    setSelectAll(false);
    setActiveHoldId(holdId);
  };

  const removeHold = (holdId) => {
    setHolds((prev) => prev.filter((h) => h.id !== holdId));

    if (activeHoldId === holdId) {
      setActiveHoldId(null);
      setCartItems([]);
      setSelectedItems([]);
      setSelectAll(false);
    }
  };

  useEffect(() => {
    const fetchAccount = async () => {
      if (!token) return;

      try {
        const res = await fetch(`${API_URL}/api/account`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });

        const json = await res.json();

        if (!res.ok) {
          console.error("Account fetch failed:", res.status, json);
          return;
        }

        const acc = json?.data;
        const fullName = `${acc?.name || ""} ${acc?.surname || ""}`.trim();

        setCashierName(fullName || acc?.name || seller || "");
      } catch (e) {
        console.error("Ошибка /api/account:", e);
        setCashierName(seller || "");
      }
    };

    fetchAccount();
  }, [token, seller]);

  const removeFromCart = useCallback((id) => {
    setCartItems((prev) => prev.filter((p) => p.id !== id));
    setSelectedItems((prev) => prev.filter((x) => x !== id));

    setDiscountSelectedId((prev) => (prev === id ? null : prev));
    setLastAddedId((prev) => (prev === id ? null : prev));
  }, []);

  useEffect(() => {
    if (!discountSelectedId) return;

    const exists = cartItems.some((x) => x.id === discountSelectedId);
    if (!exists) setDiscountSelectedId(null);
  }, [cartItems, discountSelectedId]);

  const fetchStocksByCategory = useCallback(
    async (categoryId, title) => {
      if (!token || !warehouse?.id) return;

      setLoadingCategoryProducts(true);
      setCategoryProducts([]);
      setSelectedCategory({ id: categoryId, title });
      setRightMode("products");

      try {
        const url = `${API_URL}/api/stocks?storage_id=${warehouse.id}&category_id=${categoryId}`;

        const res = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const json = await res.json();

        const list = Array.isArray(json?.data) ? json.data : [];

        setCategoryProducts(
          list.map((item) => ({
            id: item.product_id,
            name: item.name_ru,
            ntin: item.ntin,
            price: Number(item.price) || 0,
            quantity: Number(item.quantity) || 0,
            unit: item.measure_unit_id,
            category_id: item.category_id,
          }))
        );
      } catch (e) {
        console.error("Ошибка загрузки товаров по категории:", e);
        setCategoryProducts([]);
      } finally {
        setLoadingCategoryProducts(false);
      }
    },
    [token, warehouse]
  );

  const backToCategories = () => {
    setRightMode("categories");
    setSelectedCategory(null);
    setCategoryProducts([]);
  };

  const fetchCategories = useCallback(async () => {
    if (!token) return;

    setLoadingCategories(true);
    try {
      const res = await fetch(`${API_URL}/api/product_categories`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await res.json();
      const list = Array.isArray(json?.data) ? json.data : [];

      setCategories(
        list
          .filter((c) => c?.id && c?.title)
          .map((c) => ({ id: c.id, title: c.title, parent_id: c.parent_id }))
      );
    } catch (e) {
      console.error("Ошибка загрузки категорий:", e);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  }, [token]);

  const fetchActualStockQty = useCallback(
    async (product) => {
      if (!token || !warehouse?.id) return null;

      try {
        let url = null;

        if (product?.id) {
          url = `${API_URL}/api/stocks?storage_id=${warehouse.id}&product_id=${product.id}`;
        } else if (product?.ntin) {
          url = `${API_URL}/api/stocks?storage_id=${warehouse.id}&ntin=${encodeURIComponent(
            product.ntin
          )}`;
        }

        if (!url) {
          const fallbackQty = Number(product?.quantity ?? 0);
          return Number.isFinite(fallbackQty) ? fallbackQty : 0;
        }

        const res = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const json = await res.json();
        const item = json?.data?.[0];

        const qty = item?.quantity ?? 0;
        return Number(qty) || 0;
      } catch (e) {
        console.error("Ошибка проверки остатка:", e);
        const fallbackQty = Number(product?.quantity ?? 0);
        return Number.isFinite(fallbackQty) ? fallbackQty : 0;
      }
    },
    [token, warehouse]
  );

  const addToCart = useCallback((product) => {
    setLastAddedId(product.id);

    setCartItems((prev) => {
      const exist = prev.find((p) => p.id === product.id);

      if (exist) {
        if (exist.qty < product.quantity) {
          return prev.map((p) =>
            p.id === product.id ? { ...p, qty: p.qty + 1 } : p
          );
        } else {
          alert("На складе больше нет этого товара");
          return prev;
        }
      }

      return [...prev, { ...product, qty: 1 }];
    });
  }, []);

  const openReceivingConfirm = useCallback((items) => {
    const unique = [];
    const seen = new Set();

    for (const it of items || []) {
      if (!it?.id) continue;
      if (seen.has(it.id)) continue;
      seen.add(it.id);
      unique.push({ id: it.id, name: it.name, ntin: it.ntin });
    }

    setReceivingDraftItems(unique);
    setReceivingConfirmOpen(true);
  }, []);

  const handleAddProduct = useCallback(
    async (product) => {
      if (!product) return;

      const actualQty = await fetchActualStockQty(product);
      const qtyToUse = Number(actualQty ?? 0);

      if (qtyToUse <= 0) {
        openReceivingConfirm([
          { id: product.id, name: product.name, ntin: product.ntin },
        ]);
        return;
      }

      addToCart({ ...product, quantity: qtyToUse });
    },
    [addToCart, fetchActualStockQty, openReceivingConfirm]
  );

  // ✅ FIX: useCallback, чтобы deps в useEffect были корректные (и eslint не ругался)
  const fetchProductByBarcode = useCallback(
    async (barcode) => {
      if (!token || !warehouse?.id) return;

      try {
        const url = `${API_URL}/api/stocks?storage_id=${warehouse.id}&ntin=${barcode}`;
        const res = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const json = await res.json();

        if (json?.data && json.data.length > 0) {
          const product = {
            id: json.data[0].product_id,
            name: json.data[0].name_ru,
            price: parseFloat(json.data[0].price) || 0,
            quantity: json.data[0].quantity,
            unit: json.data[0].measure_unit_id,
            ntin: json.data[0].ntin,
          };

          await handleAddProduct(product);
        } else {
          alert("Товара с таким штрихкодом нет");
        }
      } catch (err) {
        console.error(err);
      }
    },
    [token, warehouse, handleAddProduct]
  );

  // ✅ FIX: deps теперь корректные (fetchProductByBarcode)
  useEffect(() => {
    let buffer = "";

    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      if (e.key === "Enter") {
        const code = buffer.trim();
        if (code) {
          fetchProductByBarcode(code);
        }
        buffer = "";
      } else if (/^[0-9]+$/.test(e.key)) {
        buffer += e.key;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [fetchProductByBarcode]);

  useEffect(() => {
    syncActiveHold(cartItems);
  }, [cartItems, syncActiveHold]);

  useEffect(() => {
    const checkActiveShift = async () => {
      if (!token || !warehouse?.id) return;

      try {
        const url = `${API_URL}/api/shifts?status=active&storage_id=${warehouse.id}`;
        const res = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const json = await res.json();

        if (json?.data?.length > 0) {
          setShiftOpen(false);
          console.log("Смена уже открыта:", json.data[0]);
        } else {
          setShiftOpen(true);
        }
      } catch (err) {
        console.error("Ошибка при проверке смены:", err);
        setShiftOpen(true);
      }
    };

    checkActiveShift();
  }, [warehouse, token]);

  // ⚠️ У тебя было ДВА одинаковых useEffect с fetchStocks (поиск) — оставляю ОДИН (с поддержкой ntin / name_ru),
  // чтобы не было двойных запросов и гонки состояний. Логику не ломает: поведение поиска станет стабильнее.
  useEffect(() => {
    const fetchStocks = async () => {
      if (!token || !warehouse?.id) {
        console.error("Нет токена или склада");
        return;
      }

      const q = (searchQuery || "").trim();

      if (q.length < 3) {
        setSearchResults([]);
        return;
      }

      const isDigitsOnly = /^[0-9]+$/.test(q);

      setLoadingSearch(true);

      try {
        const param = isDigitsOnly ? "ntin" : "name_ru";
        const url = `${API_URL}/api/stocks?storage_id=${warehouse.id}&${param}=${encodeURIComponent(
          q
        )}`;

        const res = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const json = await res.json();

        const stocksData =
          json?.data?.map((item) => ({
            id: item.product_id,
            name: item.name_ru,
            ntin: item.ntin,
            price: parseFloat(item.price) || 0,
            quantity: item.quantity,
            unit: item.measure_unit_id,
          })) || [];

        setSearchResults(stocksData);
        localStorage.setItem("stocks", JSON.stringify(stocksData));
      } catch (err) {
        console.error("Ошибка загрузки остатков:", err);

        const saved = localStorage.getItem("stocks");
        if (saved) setSearchResults(JSON.parse(saved));
      } finally {
        setLoadingSearch(false);
      }
    };

    fetchStocks();
  }, [searchQuery, warehouse, token]);

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
      setSelectAll(false);
      setDiscountSelectedId(null); // сняли всё → возврат к lastAdded
    } else {
      const ids = cartItems.map((item) => item.id);
      setSelectedItems(ids);
      setSelectAll(true);

      // если выделили все — скидку не фиксируем (нужно 1 товар)
      setDiscountSelectedId(null);
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];

      // логика выбора товара для скидки:
      if (next.length === 1) {
        setDiscountSelectedId(next[0]); // ровно 1 выбран → это цель скидки
      } else if (next.length === 0) {
        setDiscountSelectedId(null); // ничего не выбрано → вернёмся к lastAdded
      } else {
        setDiscountSelectedId(null); // много выбрано → не фиксируем
      }

      return next;
    });
  };

  useEffect(() => {
    if (cartItems.length > 0 && selectedItems.length === cartItems.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedItems, cartItems]);

  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const handleOpenShift = async (amount) => {
    if (!token) return;

    const url = `${API_URL}/api/shifts`;
    const payload = { storage_id: warehouse?.id };

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || "Смена для этого склада уже открыта");
      }

      if (data?.status === "shift_already_opened") {
        setShiftError("Смена для этого склада уже открыта");
        setTimeout(() => setShiftError(null), 5000);
        return;
      }

      setShiftOpen(false);
      setShiftError(null);
    } catch (err) {
      console.error("🔥 FETCH ERROR:", err);
      setShiftError(err.message);
    }
  };

  useEffect(() => {
    if (!split) return;

    if (categories.length === 0) fetchCategories();
  }, [split, categories.length, fetchCategories]);

  useEffect(() => {
    setSelectedItems((prev) => {
      const ids = new Set(cartItems.map((x) => x.id));
      return prev.filter((id) => ids.has(id));
    });
  }, [cartItems]);

  const baseTotal = cartItems.reduce((sum, item) => sum + calcLineBase(item), 0);
  const total = cartItems.reduce((sum, item) => sum + calcLineTotal(item), 0);
  const commissionAmount = (() => {
    const c = selectedCommission;
    if (!c) return 0;
    if (c.kind === "percent") return (total * Number(c.value || 0)) / 100;
    return Number(c.value || 0);
  })();

  const grandTotal = Math.max(0, total + commissionAmount);
  const hasDiscount = total !== baseTotal;

  const clearCart = () => {
    if (cartItems.length === 0) return;

    const confirmDelete = window.confirm(
      "Вы уверены, что хотите удалить все товары из чека?"
    );

    if (!confirmDelete) return;

    setCartItems([]);
    setSelectedItems([]);
    setSelectAll(false);
    setLastAddedId(null);
  };

  if (!warehouse) return null;

  return (
    <div className={styles.salesRoot}>
      {shiftOpen && (
        <OpenShiftModal
          warehouse={warehouse}
          onClose={onClose}
          onOpenShift={handleOpenShift}
        />
      )}

      <ReceivingConfirmModal
        open={receivingConfirmOpen}
        items={receivingDraftItems}
        onCancel={() => {
          setReceivingConfirmOpen(false);
          setReceivingDraftItems([]);
        }}
        onConfirm={() => {
          setReceivingConfirmOpen(false);
          setActiveTab("receiving");
        }}
      />

      <FastProductsModal
        open={fastProductsOpen}
        onClose={() => setFastProductsOpen(false)}
        warehouseId={warehouse.id}
        onAddProduct={async (row) => {
          await handleAddProduct(row);
          setFastProductsOpen(false);
        }}
      />

      <div className={`${styles.canvas} ${menuOpen ? styles.canvasShift : ""}`}>
        <div className={styles.header}>
          <div className={styles.left}>
            <img src="/iconGM.png" alt="Логотип" className={styles.logo} />

            <div className={styles.infoItem}>
              <FiMapPin className={styles.icon} />
              <div>
                <div className={styles.infoTitle}>{warehouse.name}</div>
                <div className={styles.infoSubtitle}>Склад</div>
              </div>
            </div>

            <div className={styles.infoItem}>
              <FiUser className={styles.icon} />
              <div>
                <div className={styles.infoTitle}>
                  {cashierName || seller || "—"}
                </div>
                <div className={styles.infoSubtitle}>Кассир</div>
              </div>
            </div>

            <div className={styles.infoItem}>
              <FiClock className={styles.icon} />
              <div>
                <div className={styles.infoTitle}>
                  {time.toLocaleTimeString().slice(0, 5)}
                </div>
                <div className={styles.infoSubtitle}>Время</div>
              </div>
            </div>
          </div>

          <div className={styles.right}>
            {["Продажа", "Приемка", "История", "X-отчет"].map((item, idx) => {
              const value = ["sale", "receiving", "history", "xreport"][idx];
              return (
                <button
                  key={item}
                  className={`${styles.navBtn} ${
                    activeTab === value ? styles.active : ""
                  }`}
                  onClick={() => setActiveTab(value)}
                >
                  {item}
                </button>
              );
            })}

            {!menuOpen && (
              <button
                className={styles.burger}
                onClick={() => setMenuOpen(true)}
              >
                <FiMenu size={22} />
              </button>
            )}
          </div>
        </div>

        {activeTab === "sale" && (
          <div className={styles.body}>
            <div className={styles.searchBar}>
              <div className={styles.searchInputWrap}>
                <FiSearch className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Поиск товара..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />

                {/* ✅ чтобы loadingSearch не был "unused" и пользователю полезно */}
                {loadingSearch && (
                  <div style={{ padding: 6, fontSize: 12, opacity: 0.7 }}>
                    Загрузка...
                  </div>
                )}

                {searchQuery.length >= 3 && searchResults.length > 0 && (
                  <div className={styles.searchDropdown}>
                    {searchResults.map((product) => (
                      <div
                        key={product.id}
                        className={styles.searchItem}
                        onClick={async () => {
                          await handleAddProduct(product);
                          setSearchQuery("");
                          setSearchResults([]);
                        }}
                      >
                        <span>{product.name}</span>
                        <span className={styles.price}>
                          {Number(product.price || 0).toFixed(2)} ₸
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={styles.searchActions}>
                <button
                  type="button"
                  className={styles.actionBtn}
                  onClick={() => setKeyboardOpen(true)}
                >
                  <FiType />
                </button>

                <KeyboardPage
                  open={keyboardOpen}
                  value={searchQuery}
                  onChange={(v) => setSearchQuery(v)}
                  onClose={() => setKeyboardOpen(false)}
                />

                <button
                  className={styles.actionBtn}
                  onClick={() => setSplit(!split)}
                >
                  <FiColumns />
                </button>
              </div>
            </div>

            {holds.length > 0 && (
              <div className={styles.holdsBar}>
                {holds.map((hold) => (
                  <div
                    key={hold.id}
                    className={`${styles.holdTab} ${
                      activeHoldId === hold.id ? styles.activeHold : ""
                    }`}
                    onClick={() => switchHold(hold.id)}
                  >
                    <span>{hold.title}</span>
                    <button
                      className={styles.holdClose}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeHold(hold.id);
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div
              className={`${styles.itemsWrapper} ${split ? styles.split : ""} ${
                cartItems.length > 0 ? styles.inlineBlock : ""
              }`}
            >
              <div className={styles.itemsList}>
                {cartItems.length === 0 ? (
                  <div className={styles.empty}>
                    <img src="/empty.png" alt="Пусто" />
                    <p>Здесь пока пусто</p>
                  </div>
                ) : (
                  <table className={styles.cartTable}>
                    <thead>
                      <tr>
                        <th>
                          <label className={styles.checkboxContainer}>
                            <input
                              type="checkbox"
                              checked={selectAll}
                              onChange={handleSelectAll}
                            />
                            <span className={styles.customCheckbox}></span>
                          </label>
                        </th>
                        <th>#</th>
                        <th>Наименование</th>
                        <th>Цена</th>
                        <th>Количество</th>
                        <th>Сумма</th>
                        <th>Удаление</th>
                      </tr>
                    </thead>

                    <tbody>
                      {cartItems.map((item, idx) => (
                        <tr key={item.id}>
                          <td>
                            <label className={styles.checkboxContainer}>
                              <input
                                type="checkbox"
                                checked={selectedItems.includes(item.id)}
                                onChange={() => handleSelectItem(item.id)}
                              />
                              <span className={styles.customCheckbox}></span>
                            </label>
                          </td>

                          <td>{idx + 1}</td>
                          <td>{item.name}</td>
                          <td>
                            <span>{Number(item.price || 0).toFixed(2)} ₸</span>

                            {item.discount?.type === "percent" &&
                              item.discount?.value > 0 && (
                                <span
                                  style={{ color: "#e53935", marginLeft: 6 }}
                                >
                                  -{item.discount.value}%
                                </span>
                              )}

                            {item.discount?.type === "amount" &&
                              item.discount?.value > 0 && (
                                <span
                                  style={{ color: "#e53935", marginLeft: 6 }}
                                >
                                  -{Number(item.discount.value).toFixed(2)} ₸
                                </span>
                              )}
                          </td>

                          <td>
                            <div className={styles.qtyContainer}>
                              <button
                                className={styles.qtyBtn}
                                style={{
                                  backgroundColor: "rgba(29, 135, 82, 1)",
                                  color: "#fff",
                                }}
                                onClick={() => {
                                  if (item.qty > 1) {
                                    setCartItems((prev) =>
                                      prev.map((p) =>
                                        p.id === item.id
                                          ? { ...p, qty: p.qty - 1 }
                                          : p
                                      )
                                    );
                                  }
                                }}
                              >
                                -
                              </button>

                              <span className={styles.qtyValue}>{item.qty}</span>

                              <button
                                className={styles.qtyBtn}
                                style={{
                                  backgroundColor: "rgba(29, 135, 82, 1)",
                                  color: "#fff",
                                }}
                                onClick={() => {
                                  if (item.qty < item.quantity) {
                                    setCartItems((prev) =>
                                      prev.map((p) =>
                                        p.id === item.id
                                          ? { ...p, qty: p.qty + 1 }
                                          : p
                                      )
                                    );
                                  }
                                }}
                              >
                                +
                              </button>
                            </div>
                          </td>

                          <td>
                            {(() => {
                              const base = item.price * item.qty;
                              const d = item.discount;

                              if (!d || !d.value) {
                                return base.toFixed(2);
                              }

                              const qtyForDiscount = Math.min(
                                item.qty,
                                Number(d.qty || item.qty)
                              );

                              let discountSum = 0;

                              if (d.type === "percent") {
                                discountSum =
                                  (item.price * d.value) / 100 * qtyForDiscount;
                              }

                              if (d.type === "amount") {
                                discountSum = d.value * qtyForDiscount;
                              }

                              const finalSum = Math.max(0, base - discountSum);

                              return finalSum.toFixed(2);
                            })()}
                          </td>

                          <td>
                            <button
                              className={styles.deleteBtn}
                              onClick={() => removeFromCart(item.id)}
                            >
                              <FiTrash2 size={18} color="red" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {split && (
                <div className={`${styles.itemsList} ${styles.rightList}`}>
                  <div className={styles.fastOverlay}>
                    {rightMode === "categories" ? (
                      <div className={styles.emptyy}>
                        <button
                          type="button"
                          className={styles.noCategoryBtn}
                          onClick={() =>
                            fetchStocksByCategory(null, "Без категорий")
                          }
                        >
                          <span className={styles.btnText}>Без категорий</span>
                        </button>

                        <button
                          type="button"
                          className={styles.noCategoryBtn}
                          onClick={() => setFastProductsOpen(true)}
                        >
                          <span className={styles.btnText}>Быстрые товары</span>
                        </button>

                        <button
                          type="button"
                          className={styles.noCategoryBtn}
                          onClick={() => setUniversalOpen(true)}
                        >
                          <span className={styles.btnText}>
                            Универсальный товар
                          </span>
                        </button>

                        <UniversalProductModal
                          open={universalOpen}
                          onClose={() => setUniversalOpen(false)}
                          onSave={(createdItems) => {
                            setCartItems((prev) => {
                              const next = [...prev];

                              for (const it of createdItems || []) {
                                const existIndex = next.findIndex(
                                  (x) => Number(x.id) === Number(it.id)
                                );

                                if (existIndex !== -1) {
                                  next[existIndex] = {
                                    ...next[existIndex],
                                    qty: Number(next[existIndex].qty || 0) + 1,
                                  };
                                } else {
                                  next.push(it); // ✅ уже содержит id, name, price, ntin и т.д.
                                }
                              }

                              return next;
                            });

                            setUniversalOpen(false);
                          }}
                        />

                        {loadingCategories && (
                          <div style={{ padding: 8 }}>Загрузка...</div>
                        )}

                        {!loadingCategories &&
                          categories.map((cat) => (
                            <button
                              key={cat.id}
                              type="button"
                              className={styles.noCategoryBtn}
                              onClick={() =>
                                fetchStocksByCategory(cat.id, cat.title)
                              }
                            >
                              <span className={styles.btnText}>{cat.title}</span>
                            </button>
                          ))}
                      </div>
                    ) : (
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 10,
                            marginBottom: 12,
                          }}
                        >
                          <strong style={{ fontSize: 14 }}>
                            {selectedCategory?.title || "Категория"}
                          </strong>

                          <button
                            type="button"
                            className={styles.actionBtn}
                            onClick={backToCategories}
                            style={{ padding: "6px 10px" }}
                          >
                            Назад
                          </button>
                        </div>

                        {loadingCategoryProducts ? (
                          <div style={{ padding: 8 }}>Загрузка товаров...</div>
                        ) : categoryProducts.length === 0 ? (
                          <div style={{ padding: 8, opacity: 0.7 }}>
                            В этой категории нет товаров
                          </div>
                        ) : (
                          <div className={styles.emptyy}>
                            {categoryProducts.map((p) => (
                              <button
                                key={p.id}
                                type="button"
                                className={styles.noCategoryBtn}
                                onClick={async () => {
                                  await handleAddProduct(p);
                                }}
                                title={p.name}
                              >
                                <span className={styles.btnText}>{p.name}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className={styles.footer}>
              <div className={styles.footerLeft}>
                <div className={styles.footerTop}>
                  <span>Выбрано позиций:</span>
                  <strong>{selectedItems.length}</strong>
                </div>

                <div className={styles.footerActions}>
                  <button
                    className={styles.footerBtn}
                    onClick={openDiscountForLastItem}
                  >
                    <FiPercent size={50} />
                    <span>Скидка</span>
                  </button>

                  <DiscountModal
                    open={discountOpen}
                    item={discountItem}
                    onClose={() => {
                      setDiscountOpen(false);
                      setDiscountItem(null);
                    }}
                    onSave={({ itemId, discount }) => {
                      setCartItems((prev) =>
                        prev.map((p) => {
                          if (p.id !== itemId) return p;
                          return {
                            ...p,
                            discount, // {type, value, qty}
                          };
                        })
                      );

                      setDiscountOpen(false);
                      setDiscountItem(null);
                    }}
                  />

                  <button
                    className={styles.footerBtn}
                    onClick={() => {
                      if (selectedItems.length !== 1) {
                        alert("Выберите ровно 1 товар для редактирования");
                        return;
                      }
                      const id = selectedItems[0];
                      const product = cartItems.find((p) => p.id === id);
                      if (!product) return;

                      setEditingProduct(product);
                      setEditOpen(true);
                    }}
                  >
                    <FiEdit size={50} />
                    <span>Редактировать</span>
                  </button>

                  <EditProductPage
                    open={editOpen}
                    product={editingProduct}
                    onClose={() => {
                      setEditOpen(false);
                      setEditingProduct(null);
                    }}
                    onSave={(updated) => {
                      setCartItems((prev) =>
                        prev.map((p) =>
                          p.id === updated.id ? { ...p, ...updated } : p
                        )
                      );
                    }}
                  />

                  <button
                    className={`${styles.footerBtn} ${styles.danger}`}
                    onClick={clearCart}
                  >
                    <FiTrash2 size={50} />
                    <span>Удалить всё</span>
                  </button>

                  <button
                    className={styles.footerBtn}
                    onClick={() => setClientModalOpen(true)}
                  >
                    <FiUsers size={50} />
                    <span>Клиенты</span>
                  </button>

                  <ClientSelectModal
                    open={clientModalOpen}
                    onClose={() => setClientModalOpen(false)}
                    onSelectClient={(client) => {
                      setSelectedClient(client);
                    }}
                  />

                  <button
                    className={styles.footerBtn}
                    onClick={() => setCommissionModalOpen(true)}
                  >
                    <FiPercent size={50} />
                    <span>Комиссия</span>
                  </button>

                  <CommissionSelectModal
                    open={commissionModalOpen}
                    onClose={() => setCommissionModalOpen(false)}
                    selected={selectedCommission}
                    onSelect={(c) => setSelectedCommission(c)}
                  />

                  <button className={styles.footerBtn} onClick={createHold}>
                    <FiDownload size={50} />
                    <span>Отложка</span>
                  </button>
                </div>
              </div>

              <div className={styles.footerRight}>
                <div className={styles.total}>
                  <span>Итого:</span>

                  {hasDiscount ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 2,
                      }}
                    >
                      <strong>{grandTotal.toLocaleString()} ₸</strong>
                      <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>
                        Комиссия: {commissionAmount.toLocaleString()} ₸
                      </div>

                      <span
                        style={{
                          fontSize: 12,
                          color: "#e53935",
                          textDecoration: "line-through",
                        }}
                      >
                        {baseTotal.toLocaleString()} ₸
                      </span>
                    </div>
                  ) : (
                    <strong>{baseTotal.toLocaleString()} ₸</strong>
                  )}
                </div>

                <button className={styles.pay} onClick={() => setPaymentOpen(true)}>
                  ПРИНЯТЬ ОПЛАТУ
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "history" && <HistoryBody warehouse={warehouse} />}

        {activeTab === "receiving" && (
          <ReceivingBody
            warehouseId={warehouse.id}
            receivingItems={receivingDraftItems}
            onReceivingDraftUsed={() => setReceivingDraftItems([])}
          />
        )}

        {paymentOpen && (
          <PaymentModal
            open={paymentOpen}
            onClose={() => setPaymentOpen(false)}
            total={total}
            warehouse={warehouse}
            client={selectedClient}
            commission={selectedCommission}
            commissionAmount={commissionAmount}
            products={cartItems.map((item) => ({
              id: item.id,
              name: item.name,
              price: item.price,
              qty: item.qty,
              total: item.price * item.qty,
            }))}
            onSuccess={() => setCartItems([])}
          />
        )}
      </div>

      <div className={`${styles.topMenu} ${menuOpen ? styles.open : ""}`}>
        <div className={styles.topButtons}>
          <button
            className={styles.topBtn}
            onClick={() => {
              setCashInOpen(true);
              setMenuOpen(false);
            }}
          >
            <FiDownload size={60} color="rgba(24, 115, 70, 1)" />
            <span>Внести сумму</span>
          </button>

          <button
            className={styles.topBtn}
            onClick={() => {
              setCashOutOpen(true);
              setMenuOpen(false);
            }}
          >
            <FiUpload size={60} color="rgba(24, 115, 70, 1)" />
            <span>Изъять сумму</span>
          </button>

          <CashInModal
            open={cashInOpen}
            warehouse={warehouse}
            onClose={() => setCashInOpen(false)}
          />

          <CashOutModal
            open={cashOutOpen}
            warehouse={warehouse}
            onClose={() => setCashOutOpen(false)}
          />

          <button
            className={styles.topBtn}
            onClick={() => {
              setCloseShiftOpen(true);
              setMenuOpen(false);
            }}
          >
            <FiXCircle size={60} color="rgba(24, 115, 70, 1)" />
            <span>Закрыть смену</span>
          </button>

          <CloseShiftModal
            open={closeShiftOpen}
            warehouse={warehouse}
            onClose={() => setCloseShiftOpen(false)}
            onSuccess={() => {
              setCloseShiftOpen(false);
              onClose();
            }}
          />

          <button
            className={styles.topBtn}
            onClick={() => {
              setLockOpen(true);
              setMenuOpen(false);
            }}
          >
            <FiLock size={60} color="rgba(24, 115, 70, 1)" />
            <span>Заблокировать режим</span>
          </button>

          <LockScreenModal
            open={lockOpen}
            seller={cashierName || seller}
            onClose={() => setLockOpen(false)}
          />

          <button onClick={onClose} className={styles.topBtn}>
            <FiBarChart2 size={60} color="rgba(24, 115, 70, 1)" />
            <span>Админ панель</span>
          </button>
        </div>
      </div>

      <div className={`${styles.sideMenu} ${menuOpen ? styles.open : ""}`}>
        <button
          className={styles.menuClose}
          onClick={() => setMenuOpen(false)}
          aria-label="Закрыть меню"
        >
          <X size={24} />
        </button>
        <ul>
          <li>Изменить склад</li>
          <li>Тех. поддержка</li>
          <li>Список клиентов</li>
          <li>Видеоинструкция</li>
          <li>Язык интерфейса</li>
          <li>Выйти</li>
        </ul>
      </div>

      {shiftError && (
        <div className={styles.shiftError}>
          <span>{shiftError}</span>
        </div>
      )}
    </div>
  );
};

export default SalesMode;
